/**
 * Scadenze CRA Art.14(2) dei 3 step di un report ENISA, come le calcola l'srp-service
 * (`services/srp-service/src/domain/sla.ts`): unica fonte per lista, dettaglio e stepper.
 *
 *  - early warning: ancora + 24h;
 *  - notifica 72h:  ancora + 72h;
 *  - relazione finale: Actively Exploited Vulnerability → ancora + 14gg; Severe Incident →
 *    30gg dalla notifica 72h (dall'istante in cui è stata registrata o, se non ancora
 *    registrata, dalla sua scadenza ancora + 72h).
 *
 * Il clock di uno step si ferma quando lo step è registrato (`notifications[].sentAt`): da lì
 * lo step è «fatto» (in tempo o in ritardo), mai più scaduto. Un thread chiuso (resolved /
 * not_needed) ferma i clock degli step non registrati.
 */

import { categoriaDiReport } from './category.js';
import type { NotificationStep, SrpReport } from './types.js';

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

export const SLA_STEP_ORDER: NotificationStep[] = ['early_warning', 'vulnerability', 'final'];

/** Relazione finale Actively Exploited Vulnerability: 14 giorni dalla rilevazione. */
export const SLA_FINAL_AEV_MS = 14 * DAY_MS;
/** Relazione finale Severe Incident: 30 giorni dalla notifica 72h. */
export const SLA_FINAL_SEVERE_INCIDENT_MS = 30 * DAY_MS;

const SLA_EARLY_MS = 24 * HOUR_MS;
const SLA_NOTIFICATION_MS = 72 * HOUR_MS;

type ReportSla = Pick<SrpReport, 'payload'>;

/** Istante (ms) di registrazione dello step; null se non registrato o data non valida. */
export function stepRecordedAtMs(report: ReportSla, step: NotificationStep): number | null {
  const n = (report.payload.notifications ?? []).find((x) => x.step === step);
  if (!n) return null;
  const ms = new Date(n.sentAt).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/** Scadenza assoluta (ms) dello step, dall'ancora del clock CRA. */
export function stepDeadlineMs(
  report: ReportSla,
  anchorMs: number,
  step: NotificationStep,
): number {
  if (step === 'early_warning') return anchorMs + SLA_EARLY_MS;
  if (step === 'vulnerability') return anchorMs + SLA_NOTIFICATION_MS;
  if (categoriaDiReport(report.payload) !== 'severe_incident') return anchorMs + SLA_FINAL_AEV_MS;
  const notifica72h = stepRecordedAtMs(report, 'vulnerability') ?? anchorMs + SLA_NOTIFICATION_MS;
  return notifica72h + SLA_FINAL_SEVERE_INCIDENT_MS;
}

/** Finestra breve dello step per la categoria del report (etichette di lista e stepper). */
export function stepWindowLabel(report: ReportSla, step: NotificationStep): string {
  if (step === 'early_warning') return '24h';
  if (step === 'vulnerability') return '72h';
  return categoriaDiReport(report.payload) === 'severe_incident' ? '30gg dalla 72h' : '14gg';
}

/** Etichetta breve della finestra (colonna SLA della lista): `24h` / `72h` / `14gg` / `30gg`. */
export function stepShortLabel(report: ReportSla, step: NotificationStep): string {
  if (step !== 'final') return stepWindowLabel(report, step);
  return categoriaDiReport(report.payload) === 'severe_incident' ? '30gg' : '14gg';
}

/** Regola della relazione finale per la categoria (testi di aiuto). */
export function finalRuleText(report: ReportSla): string {
  return categoriaDiReport(report.payload) === 'severe_incident'
    ? 'entro 30 giorni dalla notifica 72h'
    : 'entro 14 giorni dalla rilevazione';
}
